import { supabase } from "@/integrations/supabase/client";

export interface ProductSpec {
  label: string;
  value: string;
}

export interface ProductCategory {
  id: string;
  slug: string;
  name: string;
  service_slug: string;
  description: string | null;
  image_url: string | null;
  seo_title: string | null;
  seo_description: string | null;
  sort_order: number;
  is_published: boolean;
}

export interface Product {
  id: string;
  category_id: string;
  slug: string;
  name: string;
  brand: string | null;
  short_description: string | null;
  description: string | null;
  image_url: string | null;
  gallery: string[];
  price_from: number | null;
  price_to: number | null;
  price_note: string | null;
  specs: ProductSpec[];
  highlights: string[];
  badge: string | null;
  is_popular: boolean;
  is_published: boolean;
  sort_order: number;
  seo_title: string | null;
  seo_description: string | null;
}

const asArray = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

export const normalizeProduct = (row: Record<string, unknown>): Product => ({
  ...(row as unknown as Product),
  gallery: asArray<string>(row['gallery']),
  specs: asArray<ProductSpec>(row['specs']),
  highlights: asArray<string>(row['highlights']),
});

const CATEGORY_FIELDS =
  "id, slug, name, service_slug, description, image_url, seo_title, seo_description, sort_order, is_published";
const PRODUCT_FIELDS =
  "id, category_id, slug, name, brand, short_description, description, image_url, gallery, price_from, price_to, price_note, specs, highlights, badge, is_popular, is_published, sort_order, seo_title, seo_description";

export async function fetchCategories(): Promise<ProductCategory[]> {
  const { data, error } = await supabase
    .from("product_categories")
    .select(CATEGORY_FIELDS)
    .eq("is_published", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ProductCategory[];
}

export async function fetchCategoryBySlug(slug: string): Promise<ProductCategory | null> {
  const { data, error } = await supabase
    .from("product_categories")
    .select(CATEGORY_FIELDS)
    .eq("slug", slug)
    .eq("is_published", true)
    .maybeSingle();
  if (error) throw error;
  return (data as ProductCategory | null) ?? null;
}

export async function fetchCategoryByService(serviceSlug: string): Promise<ProductCategory | null> {
  const { data, error } = await supabase
    .from("product_categories")
    .select(CATEGORY_FIELDS)
    .eq("service_slug", serviceSlug)
    .eq("is_published", true)
    .order("sort_order", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as ProductCategory | null) ?? null;
}

export async function fetchProducts(categoryId: string): Promise<Product[]> {
  const { data, error } = await supabase
    .from("products")
    .select(PRODUCT_FIELDS)
    .eq("category_id", categoryId)
    .eq("is_published", true)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []).map((r) => normalizeProduct(r as Record<string, unknown>));
}

export async function fetchProduct(
  categorySlug: string,
  productSlug: string,
): Promise<{ category: ProductCategory; product: Product } | null> {
  const category = await fetchCategoryBySlug(categorySlug);
  if (!category) return null;
  return fetchProductInCategory(category, productSlug);
}

/** Hämtar produkt via tjänstens slug, t.ex. /laddboxar/zaptec-go. */
export async function fetchProductByService(
  serviceSlug: string,
  productSlug: string,
): Promise<{ category: ProductCategory; product: Product } | null> {
  const category = await fetchCategoryByService(serviceSlug);
  if (!category) return null;
  return fetchProductInCategory(category, productSlug);
}

async function fetchProductInCategory(
  category: ProductCategory,
  productSlug: string,
): Promise<{ category: ProductCategory; product: Product } | null> {
  const { data, error } = await supabase
    .from("products")
    .select(PRODUCT_FIELDS)
    .eq("category_id", category.id)
    .eq("slug", productSlug)
    .eq("is_published", true)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return { category, product: normalizeProduct(data as Record<string, unknown>) };
}

/** Produkt-URL ligger direkt under tjänstekategorin, t.ex. /laddboxar/zaptec-go. */
export const productPath = (serviceSlug: string, productSlug: string) =>
  `/${serviceSlug}/${productSlug}`;

export const formatPrice = (p: Product): string | null => {
  const fmt = (n: number) => new Intl.NumberFormat("sv-SE").format(Math.round(n));
  if (p.price_from && p.price_to) return `${fmt(p.price_from)}–${fmt(p.price_to)} kr`;
  if (p.price_from) return `Från ${fmt(p.price_from)} kr`;
  if (p.price_to) return `Upp till ${fmt(p.price_to)} kr`;
  return p.price_note ?? null;
};

/** Länk till offertflödet med produktinformation som följer med till partnern. */
export const productQuoteUrl = (
  serviceSlug: string,
  category: ProductCategory,
  product: Product,
) =>
  `/${serviceSlug}/offert?services=${encodeURIComponent(serviceSlug)}&produkt=${encodeURIComponent(
    product.slug,
  )}&kategori=${encodeURIComponent(category.slug)}`;
